import React, { useState, useEffect } from 'react'
import { userService } from '../services/userService'
import { UserProfile } from '../lib/supabase'

const UserManagement: React.FC = () => {
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [zoomedPhoto, setZoomedPhoto] = useState<string | null>(null)

  useEffect(() => {
    fetchUserProfiles()
  }, [])

  const fetchUserProfiles = async () => {
    try {
      setLoading(true)
      const data = await userService.getAllUserProfiles()
      setUserProfiles(data)
    } catch (err) {
      setError('Failed to fetch user profiles')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (userProfile: UserProfile, status: UserProfile['user_status']) => {
    const id = userProfile.id
    try {
      setUpdating(id)
      await userService.updateUserStatus(id, status)
      await fetchUserProfiles() // Refresh the list
    } catch (err) {
      setError('Failed to update user profile status')
    } finally {
      setUpdating(null)
    }
  }

  const getStatusColor = (status: UserProfile['user_status']) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: UserProfile['user_status']) => {
    switch (status) {
      case 'approved':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'rejected':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'paused':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage user profiles and their status</p>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                User Profiles ({userProfiles.length})
              </h3>
              <button
                onClick={fetchUserProfiles}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Refresh
              </button>
            </div>
            
            {userProfiles.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No user profiles</h3>
                <p className="mt-1 text-sm text-gray-500">No user profiles found in the system.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userProfiles.map((userProfile) => (
                        <tr 
                          key={userProfile.id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedUser(userProfile)
                            setShowModal(true)
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                                  <span className="text-sm font-medium text-indigo-700">
                                    {userProfile.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {userProfile.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {userProfile.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{userProfile.mobile || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(userProfile.user_status)}`}>
                              {getStatusIcon(userProfile.user_status)}
                              <span className="ml-1 capitalize">{userProfile.user_status}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(userProfile.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {userProfiles.map((userProfile) => (
                    <div
                      key={userProfile.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedUser(userProfile)
                        setShowModal(true)
                      }}
                    >
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="flex-shrink-0 h-12 w-12">
                          <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-base font-medium text-indigo-700">
                              {userProfile.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-gray-900 truncate">{userProfile.name}</h3>
                          <p className="text-xs text-gray-500 truncate">ID: {userProfile.id}</p>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(userProfile.user_status)}`}>
                          {getStatusIcon(userProfile.user_status)}
                          <span className="ml-1 capitalize hidden sm:inline">{userProfile.user_status}</span>
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-600">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{userProfile.mobile || 'N/A'}</span>
                        </div>
                        <div className="flex items-center text-gray-500 text-xs pt-1">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>{formatDate(userProfile.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowModal(false)}>
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center z-10">
              <div className="flex-1 min-w-0 pr-2">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{selectedUser.name}</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">ID: {selectedUser.id}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="px-4 sm:px-6 py-4">
              <div className="space-y-4">
                {/* Profile Header with Personal Information */}
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start space-x-3 sm:space-x-4 mb-4">
                    <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 border-2 border-indigo-200">
                      {selectedUser.photo_url ? (
                        <img src={selectedUser.photo_url} alt={selectedUser.name} className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover" />
                      ) : (
                        <span className="text-2xl sm:text-3xl font-medium text-indigo-700">
                          {selectedUser.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 truncate">{selectedUser.name}</h4>
                      <div>
                        <span className={`inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedUser.user_status)}`}>
                          {getStatusIcon(selectedUser.user_status)}
                          <span className="ml-1 capitalize">{selectedUser.user_status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Personal Information in 3 columns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {selectedUser.mobile && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Mobile</label>
                        <p className="text-sm font-medium text-gray-900">{selectedUser.country_code || ''}{selectedUser.mobile}</p>
                      </div>
                    )}
                    {selectedUser.age !== undefined && selectedUser.age !== null && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Age</label>
                        <p className="text-sm font-medium text-gray-900">{selectedUser.age}</p>
                      </div>
                    )}
                    {selectedUser.gender && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                        <p className="text-sm font-medium text-gray-900 capitalize">{selectedUser.gender}</p>
                      </div>
                    )}
                    {selectedUser.occupation && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Occupation</label>
                        <p className="text-sm font-medium text-gray-900">{selectedUser.occupation}</p>
                      </div>
                    )}
                    {selectedUser.education && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Education</label>
                        <p className="text-sm font-medium text-gray-900">{selectedUser.education}</p>
                      </div>
                    )}
                                          {selectedUser.city && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                          <p className="text-sm text-gray-900">{selectedUser.city}</p>
                        </div>
                      )}
                      {selectedUser.state && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                          <p className="text-sm text-gray-900">{selectedUser.state}</p>
                        </div>
                      )}
                    {selectedUser.bio && (
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Bio</label>
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedUser.bio}</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Interests & Languages */}
                {((selectedUser.languages && selectedUser.languages.length > 0) || (selectedUser.interests && selectedUser.interests.length > 0)) && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-base font-semibold text-gray-900 mb-3">Interests & Languages</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedUser.languages && selectedUser.languages.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Languages</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedUser.languages.map((lang, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedUser.interests && selectedUser.interests.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">Interests</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedUser.interests.map((interest, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                {interest}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Photos Section */}
                {(selectedUser.photo_url || selectedUser.pending_photo_url || (selectedUser.album && selectedUser.album.length > 0)) && (
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Photos</h4>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 sm:gap-3">
                      {selectedUser.photo_url && (
                        <div 
                          className="cursor-pointer group relative aspect-square overflow-hidden rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-all"
                          onClick={() => setZoomedPhoto(selectedUser.photo_url || null)}
                        >
                          <img 
                            src={selectedUser.photo_url} 
                            alt={selectedUser.name} 
                            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-200" 
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                          <div className="absolute top-2 left-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded">Profile</div>
                        </div>
                      )}
                      {selectedUser.pending_photo_url && (
                        <div 
                          className="cursor-pointer group relative aspect-square overflow-hidden rounded-lg border-2 border-yellow-200 hover:border-yellow-400 transition-all"
                          onClick={() => setZoomedPhoto(selectedUser.pending_photo_url || null)}
                        >
                          <img 
                            src={selectedUser.pending_photo_url} 
                            alt={`${selectedUser.name} pending`} 
                            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-200" 
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                          <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">Pending</div>
                        </div>
                      )}
                      {selectedUser.album && selectedUser.album.map((photo, index) => (
                        <div
                          key={index}
                          className="cursor-pointer group relative aspect-square overflow-hidden rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-all"
                          onClick={() => setZoomedPhoto(photo)}
                        >
                          <img 
                            src={photo} 
                            alt={`${selectedUser.name} album ${index + 1}`} 
                            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-200" 
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}






              </div>
            </div>
            
            {/* Footer with Action Buttons */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Close
              </button>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedUser, 'approved')
                    if (updating !== selectedUser.id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={updating === selectedUser.id || selectedUser.user_status === 'approved'}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
                >
                  {updating === selectedUser.id ? 'Updating...' : 'Approve'}
                </button>
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedUser, 'rejected')
                    if (updating !== selectedUser.id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={updating === selectedUser.id || selectedUser.user_status === 'rejected'}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600"
                >
                  {updating === selectedUser.id ? 'Updating...' : 'Reject'}
                </button>
                <button
                  onClick={async () => {
                    await handleStatusUpdate(selectedUser, 'paused')
                    if (updating !== selectedUser.id) {
                      setShowModal(false)
                    }
                  }}
                  disabled={updating === selectedUser.id || selectedUser.user_status === 'paused'}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-yellow-600"
                >
                  {updating === selectedUser.id ? 'Updating...' : 'Pause'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Zoom Modal */}
      {zoomedPhoto && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomedPhoto(null)}
        >
          <div className="relative max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setZoomedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={zoomedPhoto} 
              alt="Zoomed photo" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
